<template>
    <div class="p-4 mx-auto w-100" style="max-width: 52rem">
        <!-- Header / Title -->
        <b-card no-body class="mb-3 shadow-sm rounded-3">
            <b-card-body
                class="d-flex align-items-center justify-content-between py-3"
            >
                <div class="d-flex align-items-center">
                    <div class="me-3">
                        <standard-avatar :user="onboardingUser" />
                    </div>
                    <div>
                        <h2 class="h4 mb-1 d-flex align-items-center">
                            <font-awesome-icon
                                icon="fa-duotone fa-bolt"
                                size="lg"
                                class="me-2"
                            />
                            Random Match
                        </h2>
                        <div class="text-muted small">
                            Me:
                            <strong class="text-dark">{{
                                onboardingUser.id
                            }}</strong>
                            <b-badge
                                v-if="published"
                                variant="info"
                                class="ms-2"
                            >
                                <font-awesome-icon
                                    icon="fa-duotone fa-hourglass-half"
                                    class="me-1"
                                />
                                Waiting
                            </b-badge>
                        </div>
                    </div>
                </div>

                <div class="text-end">
                    <b-button
                        size="sm"
                        variant="outline-secondary"
                        v-b-toggle.debugPanel
                        class="me-2"
                    >
                        <font-awesome-icon
                            icon="fa-duotone fa-bug"
                            class="me-1"
                        />
                        Debug
                    </b-button>
                    <b-button
                        size="sm"
                        variant="outline-danger"
                        @click="reset"
                        :disabled="busy"
                        v-b-tooltip.hover
                        title="Clear the current state"
                    >
                        <font-awesome-icon
                            icon="fa-duotone fa-rotate-left"
                            class="me-1"
                        />
                        Reset
                    </b-button>
                </div>
            </b-card-body>

            <!-- Status Bar -->
            <b-card-footer class="py-2 bg-light">
                <div class="d-flex align-items-center">
                    <b-spinner small class="me-2" v-if="busy" />
                    <span v-if="status" class="small">
                        <font-awesome-icon
                            icon="fa-duotone fa-info-circle"
                            class="me-1"
                        />
                        {{ status }}
                    </span>
                    <span v-else class="small text-muted">
                        <font-awesome-icon
                            icon="fa-duotone fa-circle-dot"
                            class="me-1"
                        />
                        Idle
                    </span>
                </div>
            </b-card-footer>
        </b-card>

        <!-- Matched State -->
        <b-card v-if="matchId" class="mb-4 border-success shadow-sm rounded-3">
            <b-card-body class="d-flex align-items-center">
                <div class="me-3">
                    <standard-avatar :user="{ id: matchId }" />
                    <random-chat-view :user="{ id: matchId }" />
                </div>
                <div class="flex-grow-1">
                    <h5 class="mb-1">
                        <font-awesome-icon
                            icon="fa-duotone fa-handshake"
                            class="me-2"
                        />
                        Matched with <strong>#{{ matchId }}</strong>
                    </h5>
                    <div class="text-muted small">
                        You can reset to find another match.
                    </div>
                </div>
                <div class="text-end">
                    <b-button variant="outline-danger" @click="reset">
                        <font-awesome-icon
                            icon="fa-duotone fa-xmark"
                            class="me-1"
                        />
                        Reset
                    </b-button>
                </div>
            </b-card-body>
        </b-card>

        <!-- Controls (when not matched) -->
        <b-card v-else class="mb-4 shadow-sm rounded-3">
            <b-card-body>
                <div class="d-flex flex-wrap align-items-center gap-2">
                    <b-button
                        variant="primary"
                        class="me-2 mb-2"
                        @click="runMatch"
                        :disabled="busy"
                    >
                        <b-spinner small class="me-2" v-if="busy" />
                        <font-awesome-icon
                            v-else
                            icon="fa-duotone fa-magnifying-glass"
                            class="me-2"
                        />
                        {{ busy ? "Matching..." : "Find Match" }}
                    </b-button>

                    <b-button
                        variant="outline-primary"
                        class="mb-2"
                        @click="publishSelf"
                        :disabled="busy || published"
                    >
                        <font-awesome-icon
                            :icon="
                                published
                                    ? 'fa-duotone fa-hourglass-half'
                                    : 'fa-duotone fa-right-to-bracket'
                            "
                            class="me-2"
                        />
                        {{ published ? "Waiting..." : "Enter Queue" }}
                    </b-button>
                </div>

                <div class="mt-3">
                    <b-progress height="0.5rem">
                        <b-progress-bar
                            :value="published ? 50 : 0"
                        ></b-progress-bar>
                        <b-progress-bar
                            :value="matchId ? 50 : 0"
                            variant="success"
                        ></b-progress-bar>
                    </b-progress>
                    <div
                        class="d-flex justify-content-between small text-muted mt-1"
                    >
                        <span>Queued</span>
                        <span>Matched</span>
                    </div>
                </div>
            </b-card-body>
        </b-card>

        <!-- Candidates Pretty List -->
        <b-card v-if="candidates.length" class="mb-4 shadow-sm rounded-3">
            <b-card-header
                class="d-flex align-items-center justify-content-between"
            >
                <div class="d-flex align-items-center">
                    <font-awesome-icon
                        icon="fa-duotone fa-users"
                        class="me-2"
                    />
                    <strong>Candidates</strong>
                    <b-badge variant="secondary" class="ms-2">{{
                        candidates.length
                    }}</b-badge>
                </div>
                <small class="text-muted"
                    >Tap skip to temporarily hide a user</small
                >
            </b-card-header>
            <b-list-group flush>
                <b-list-group-item
                    v-for="id in candidates"
                    :key="id"
                    class="d-flex align-items-center"
                >
                    <div class="me-3">
                        <standard-avatar :user="{ id }" />
                    </div>
                    <div class="flex-grow-1">
                        <div class="fw-semibold">User #{{ id }}</div>
                        <div class="text-muted small">
                            Potential match candidate
                        </div>
                    </div>
                    <div class="d-flex align-items-center">
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            class="me-2"
                            @click="skipCandidate(id, 30)"
                            v-b-tooltip.hover
                            title="Skip for 30 minutes"
                        >
                            <font-awesome-icon
                                icon="fa-duotone fa-clock"
                                class="me-1"
                            />
                            30m
                        </b-button>
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            class="me-2"
                            @click="skipCandidate(id, 120)"
                            v-b-tooltip.hover
                            title="Skip for 2 hours"
                        >
                            <font-awesome-icon
                                icon="fa-duotone fa-clock"
                                class="me-1"
                            />
                            2h
                        </b-button>
                        <b-button
                            size="sm"
                            variant="outline-danger"
                            @click="skipCandidate(id, 1440)"
                            v-b-tooltip.hover
                            title="Skip for 24 hours"
                        >
                            <font-awesome-icon
                                icon="fa-duotone fa-ban"
                                class="me-1"
                            />
                            24h
                        </b-button>
                    </div>
                </b-list-group-item>
            </b-list-group>
        </b-card>

        <!-- Debug Panel -->
        <b-collapse id="debugPanel" class="mt-3">
            <b-card no-body class="shadow-sm rounded-3">
                <b-card-header class="d-flex align-items-center">
                    <font-awesome-icon icon="fa-duotone fa-bug" class="me-2" />
                    <strong>Debug</strong>
                </b-card-header>
                <b-card-body>
                    <pre class="small bg-light p-2 rounded mb-0">{{
                        debug
                    }}</pre>
                </b-card-body>
                <b-card-footer v-if="candidates.length" class="bg-white">
                    <div class="d-flex align-items-center">
                        <font-awesome-icon
                            icon="fa-duotone fa-database"
                            class="me-2"
                        />
                        <strong>Raw Candidates:</strong>
                        <span class="small text-muted ms-2">{{
                            candidates
                        }}</span>
                    </div>
                </b-card-footer>
            </b-card>
        </b-collapse>
    </div>
</template>

<script>
import { waitingRef } from "@/firebase";
import { get } from "firebase/database";
import { getSkipMap, skipUser, isSkipped } from "@/utility/randomChat";
import { mapState } from "vuex";

import RandomChatView from "@/components/randomChat/RandomChatView";

export default {
    created() {
        getSkipMap();
    },
    mounted() {
        // Only handle the exact actions you named:
        // - randomChat/handleCompatibleUsers
        // - randomChat/handleMatch         (incoming match via push)
        // - randomChat/handleVacate        (peer left via push)
        this.unsubscribeToAction = this.$store.subscribeAction({
            after: async (action) => {
                if (action.type === "randomChat/handleCompatibleUsers") {
                    if (this.isProcessingCompatibleUsers || this.matchId)
                        return;
                    this.isProcessingCompatibleUsers = true;

                    const { compatibleUserIds } = action.payload || {};
                    this.debug += `\ncompatibleIds: ${JSON.stringify(
                        compatibleUserIds
                    )}`;

                    if (
                        !Array.isArray(compatibleUserIds) ||
                        compatibleUserIds.length === 0
                    ) {
                        this.status =
                            "No compatible candidates. You can enter queue.";
                        this.isProcessingCompatibleUsers = false;
                        return;
                    }

                    if (this.isClaimingCandidate) {
                        this.isProcessingCompatibleUsers = false;
                        return;
                    }

                    this.status = "Attempting claim...";
                    this.isClaimingCandidate = true;
                    const claimedUserId = await this.tryClaimInOrder(
                        compatibleUserIds
                    );
                    this.isClaimingCandidate = false;
                    this.isProcessingCompatibleUsers = false;

                    if (claimedUserId != null) {
                        this._setMatched(claimedUserId, "claim-success");
                    } else {
                        this.status = "Claim failed for all. Entering queue...";
                        await this.publishSelf();
                    }
                    return;
                }

                if (action.type === "randomChat/handleMatch") {
                    if (this.matchId) return;
                    const otherId = action.payload?.fromUserId;
                    if (otherId == null) {
                        this.debug += `\n[warn] handleMatch missing fromUserId`;
                        return;
                    }
                    this._setMatched(otherId, "push-match");
                    return;
                }

                if (action.type === "randomChat/handleVacate") {
                    // Peer left broadcast. Only clear if it is our current partner.
                    const fromId = action.payload?.fromUserId;
                    if (
                        this.matchId &&
                        Number(fromId) === Number(this.matchId)
                    ) {
                        this.debug += `\n[vacate] peer ${fromId} left`;
                        this._teardownSessionUI("Partner left");
                    } else {
                        this.debug += `\n[vacate] ignored (fromId:${fromId})`;
                    }
                    return;
                }
            },
        });
    },
    beforeDestroy() {
        if (this.unsubscribeToAction) this.unsubscribeToAction();
    },
    data() {
        return {
            status: "",
            busy: false,
            published: false,
            candidates: [],
            matchId: null,
            unsubscribeToAction: null,
            debug: "",
            isProcessingCompatibleUsers: false,
            isClaimingCandidate: false,
            hasAttemptedPublishSelf: false,
        };
    },
    computed: {
        ...mapState({
            onboardingUser: (state) => state.onboarding.user,
        }),
    },
    methods: {
        _setMatched(otherUserId, source = "") {
            this.matchId = otherUserId;
            this.published = false;
            this.status = `Matched with ${otherUserId}`;
            if (source) this.debug += `\n[match] ${source}: ${otherUserId}`;
        },
        _teardownSessionUI(message = "Session ended") {
            this.status = message;
            this.matchId = null;
            // keep published=false; user chooses to re-queue
        },

        // fictional endpoint you’ll implement; used when *we* leave
        async leaveSession() {
            try {
                await this.api.post(`/randomChat/${this.matchId}/vacate`);
            } catch (e) {
                this.debug += `\n[leaveSession:error] ${e?.message || e}`;
            }
        },

        async runMatch() {
            this.busy = true;
            this.status = "Fetching queue...";
            this.debug = "";
            try {
                const allWaitingUserIds = await this.fetchWaitingOnce();
                this.candidates = allWaitingUserIds.filter(
                    (id) => id !== this.onboardingUser.id
                );
                const eligibleUserIds = this.candidates.filter(
                    (id) => !isSkipped(id)
                );

                if (!eligibleUserIds.length) {
                    this.status = "No eligible candidates (all skipped).";
                    this.busy = false;
                    return;
                }

                this.status = "Checking compatibility...";
                await this.api.post("/compatible/users", {
                    module: "randomChat",
                    otherUserIds: eligibleUserIds,
                });
            } catch (e) {
                this.status = `Error: ${e.message || e}`;
            } finally {
                this.busy = false;
            }
        },

        async fetchWaitingOnce() {
            const snapshot = await get(waitingRef);
            const value = snapshot.val();
            if (!Array.isArray(value)) return [];
            const normalized = value.map((v) =>
                typeof v === "string" ? parseInt(v, 10) : v
            );
            return normalized.filter((v) => Number.isFinite(v));
        },

        async tryClaimInOrder(candidateIds) {
            for (const candidateId of candidateIds) {
                const { data } = await this.api.post(
                    `/randomChat/${candidateId}/removeFromQueue`
                );
                if (data && data.success) return candidateId;
            }
            return null;
        },

        async publishSelf() {
            if (this.hasAttemptedPublishSelf || this.published) return;
            this.hasAttemptedPublishSelf = true;
            this.status = "Adding self to queue...";
            try {
                const { data } = await this.api.post("/randomChat/enterQueue", {
                    userId: this.onboardingUser.id,
                });
                if (data && data.success) {
                    this.published = true;
                    this.status = "Waiting for assignment.";
                } else {
                    this.status = "Failed to publish self.";
                }
            } catch (e) {
                this.status = `Failed to publish self: ${e.message || e}`;
            }
        },

        skipCandidate(userId, minutes = 30) {
            const untilTimestamp = skipUser(userId, minutes);
            this.status = `Skipped ${userId} until ${new Date(
                untilTimestamp
            ).toLocaleTimeString()}`;
        },

        async reset() {
            this.busy = true;
            try {
                if (this.matchId) {
                    await this.leaveSession("user-reset");
                }
            } catch (e) {
                this.debug += `\n[reset:leave error] ${e?.message || e}`;
            } finally {
                this.status = "";
                this.busy = false;
                this.published = false;
                this.candidates = [];
                this.matchId = null;
                this.debug = "";
                this.isProcessingCompatibleUsers = false;
                this.isClaimingCandidate = false;
                this.hasAttemptedPublishSelf = false;
            }
        },
    },
    components: {
        RandomChatView,
    },
};
</script>
